import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { identityDisplayName } from '@/components/identities/identity-display-name'
import {
  IdentityManagerView,
  type IdentityStats,
  type PersonWithDocuments,
} from '@/components/identity/IdentityManagerView'
import { GeneratePdfMenu } from '@/components/pdf-generation/GeneratePdfMenu'
import { identityPath } from '@/features/identities/identity-path'
import { useDebounced } from '@/hooks/use-debounced'
import { ADVANCED_FACE_DETECTION_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { ApiError, api } from '@/services/api'

interface IdentityManagerProps {
  caseId: string
  onViewIdentity?: (identityId: string) => void
}

/**
 * Connected identity manager. Owns the case-scoped fetch, search debounce, and
 * server pagination, then hands derived data + handlers to the pure
 * {@link IdentityManagerView}.
 */
export function IdentityManager({ caseId, onViewIdentity }: IdentityManagerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const faceDetection = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  const [stats, setStats] = useState<IdentityStats | null>(null)
  const [persons, setPersons] = useState<PersonWithDocuments[]>([])
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [isLoadingPersons, setIsLoadingPersons] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [filteredTotal, setFilteredTotal] = useState(0)
  const pageSize = 20

  // Debounce search input — only hit the API after the user stops typing.
  const debouncedQuery = useDebounced(searchQuery, 250).trim()

  // Reset to first page whenever the active query changes.
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedQuery])

  // Fetch data from case-specific database
  const fetchData = useCallback(async () => {
    setIsLoadingStats(true)
    setIsLoadingPersons(true)

    try {
      const data = (await api.identities.list(caseId, {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        q: debouncedQuery || undefined,
      })) as unknown as {
        persons?: Array<{
          id: string
          name: string
          nickname?: string | null
          unnamedSeq?: number | null
          properties: Record<string, unknown>
          source_documents: string[]
          relationship_types: string[]
          score?: number
          documentCount?: number
          relationshipCount?: number
          idDocumentCount?: number
          hasVerifiedDocument?: boolean
        }>
        stats?: {
          totalPersons: number
          withVerifiedDocuments: number
          withPossibleDocuments: number
        }
        total?: number
      }

      const personsList: PersonWithDocuments[] = (data.persons || []).map(p => ({
        id: p.id,
        // A manual person may have no name at all — the label is composed
        // here and never stored (PRD-0031 slice 04).
        name: identityDisplayName(
          { name: p.name, nickname: p.nickname, unnamedSeq: p.unnamedSeq },
          t
        ),
        nickname: p.nickname ?? undefined,
        // Main photo of the person — the row thumbnail follows the chosen face
        // (or the ID-document default) as soon as the list is re-read.
        photoPath:
          (p.properties?.photo_path as string) || (p.properties?.photoPath as string) || undefined,
        personalNumber:
          (p.properties?.personalNumber as string) ||
          (p.properties?.personal_number as string) ||
          undefined,
        birthDate:
          (p.properties?.birthDate as string) ||
          (p.properties?.birth_date as string) ||
          (p.properties?.dateOfBirth as string) ||
          undefined,
        isVerified:
          p.hasVerifiedDocument ??
          (p.relationship_types?.some(
            rel => rel === 'HAS_ID_DOCUMENT' || rel === 'HAS_IDENTIFICATION_DOCUMENT'
          ) ||
            false),
        documentCount: p.documentCount ?? p.source_documents?.length ?? 0,
        relationshipCount: p.relationshipCount ?? 0,
        idDocumentCount: p.idDocumentCount ?? 0,
        score: p.score ?? 0,
      }))

      setPersons(personsList)
      setFilteredTotal(data.total ?? personsList.length)

      if (data.stats) {
        setStats({
          totalPersons: data.stats.totalPersons,
          withVerifiedDocuments: data.stats.withVerifiedDocuments,
          withPossibleDocuments: data.stats.withPossibleDocuments,
        })
      } else {
        const verified = personsList.filter(p => p.isVerified).length
        const possible = personsList.filter(p => !p.isVerified && p.documentCount > 0).length
        setStats({
          totalPersons: data.total || personsList.length,
          withVerifiedDocuments: verified,
          withPossibleDocuments: possible,
        })
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setPersons([])
        setFilteredTotal(0)
        setStats({ totalPersons: 0, withVerifiedDocuments: 0, withPossibleDocuments: 0 })
        return
      }
      console.error('Error fetching identities:', err)
    } finally {
      setIsLoadingStats(false)
      setIsLoadingPersons(false)
    }
  }, [caseId, currentPage, debouncedQuery, t])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize))

  // Navigate to identity detail or call callback if provided
  const handlePersonClick = (personId: string) => {
    if (onViewIdentity) {
      onViewIdentity(personId)
    } else {
      navigate(identityPath(caseId, personId))
    }
  }

  return (
    <IdentityManagerView
      summary={{ stats, isLoading: isLoadingStats }}
      listing={{
        persons,
        isLoading: isLoadingPersons,
        searchQuery,
        currentPage,
        totalPages,
        filteredTotal,
        pageSize,
      }}
      actions={{
        renderPersonActions: personId => (
          <GeneratePdfMenu subjectType="identity" subjectRef={personId} caseId={caseId} />
        ),
        onSearchChange: value => {
          setSearchQuery(value)
          setCurrentPage(1)
        },
        onSearchSubmit: () => setCurrentPage(1),
        onRefresh: () => void fetchData(),
        onPersonClick: handlePersonClick,
        onPreviousPage: () => setCurrentPage(p => Math.max(1, p - 1)),
        onNextPage: () => setCurrentPage(p => Math.min(totalPages, p + 1)),
      }}
      faceDetection={faceDetection.enabled}
    />
  )
}
